import AbstractSearchableObject from './rz-status_searchObject'

enum Impact { Ready, Limited, Outage };
enum ServiceState { Available, Maintanance, Limited, Outage };
const ServiceStateNumbers = Object.values(ServiceState).filter(value => typeof value === 'number')
    
interface IParser {
    parseIncidents(incidentsJson: string): IIncident[];
    parseMaintanances(maintanancesJson: string): IMaintanance[];
    parseExternalIncidents(externalJson: string): IExternalIncident[];
    parseNotifications(notificationJson: string): IGlobalMessage[];
}

enum FileType {
        INCIDENTS,
        PAST_INCIDENTS,
        MAINTENANCES,
        NEWS,
        EXTERNAL_INCIDENTS
}
interface IFileState {
    fileType:FileType;
    lastModified:Date;
    isChanged(date:Date):boolean;
}

interface ILoader {
    loadFile(url: string, callback: (file: string, error: boolean) => void, fileType:FileType): void;
}
    
interface ISlaShortInfo {
    id: number;
    name: string;
}
    
interface IMaintananceRaw {
    ticketId: string;
    descriptionLong: string;
    startDate: string;
    endDate: string;
    lastEdit: string;
    affectedServicesInfo: ISlaShortInfo[];
}

interface IDateFilterable {
    startDate: Date;
    endDate: Date;
}

interface IMaintanance extends IDateFilterable,  ISearchableObject {
    ticketId: string;
    descriptionLong: string;
    lastEdit: Date;
    affectedServicesInfo: ISlaShortInfo[];
}
    
interface IIncidentRaw extends IDateFilterable {
    serviceId: ISlaShortInfo;
    currentState: string;
    worstState: string;
    externalInfo: string;
    serviceInfoDto: {
        id: number,
        name: string
    }
}

interface IIncident extends IDateFilterable,  ISearchableObject {
    serviceId: ISlaShortInfo;
    serviceName: string;
    currentState: Impact;
    worstState: Impact;
    externalInfo: string;
}

interface IExternalIncidentRaw {
    id: string;
    title: string;
    markdown: string;
}

interface IExternalIncident {
    id: string;
    title: string;
    markdown: string;
    getHtml(): string; // ???
}

interface IGlobalMessageRaw {
    id: string;
    text: string;
    isExternal: boolean;
}

interface  IGlobalMessage {
    text: string;
    isExternal: boolean;
}

interface ICategoryRaw {
    id: number;
    name: string;
    services: IServiceRaw[];
    categories: ICategoryRaw[];
}

interface IServiceRaw {
    id: number;
    name: string;
    currentStatus: string;
}

interface ICategory {
    id: number;
    name: string;
    services: IService[];
    categories: ICategory[];
    worstState: ServiceState;
    states: Record<ServiceState, number>;
}

interface IService {
    id: number;
    name: string;
    currentStatus: ServiceState;
    incident?: IIncident;
    maintenance?: IMaintanance;
}

interface IServiceTree {
    categories: ICategory[] | null;
    incidents: IIncident[] | null;
    states: Record<ServiceState, number>
}

interface ISearchableObject {
    matchesSearch (searchTerm:string,fields:string[]):boolean;
}


class Incident extends AbstractSearchableObject implements IIncident{
    serviceId: ISlaShortInfo;
    serviceName: string;
    startDate: Date;
    endDate: Date;
    currentState: Impact;
    worstState: Impact;
    externalInfo: string;

    // Override from AbstractSearchableObject
    public matchesSearch (searchTerm:string, fields:string[]):boolean {
        for (var i:number=0; i < fields.length; i++) {
            switch (fields[i]) {
                case "Sla" : 
                    if (this.matchesFreeText(searchTerm, String(this.serviceId.name))) {return(true);}
                    break;
                case "AdditionalInfo" :
                    if ((typeof (this.externalInfo) != "undefined") && (this.matchesFreeText(searchTerm,this.externalInfo))){return(true);}
                    break;
                case "startDate" :
                    if (this.matchesDate(searchTerm, this.startDate)) {return(true);}
                    break;
                case "endDate" :
                    if (this.matchesDate(searchTerm, this.endDate)) {return(true);}
                    break;
                default :
                    break;
            }
        }
        return (false);
    }
}


class Maintanance extends AbstractSearchableObject implements IMaintanance {
    ticketId:string;
    descriptionLong: string;
    startDate: Date;
    endDate: Date;
    lastEdit: Date;
    affectedServicesInfo: ISlaShortInfo[];

    // Override from AbstractSearchableObject
    public matchesSearch (searchTerm:string, fields:string[]):boolean {

        for (var i:number=0; i < fields.length; i++) {            
            switch (fields[i]) {          
                case "startDate" :
                    if (this.matchesDate(searchTerm, this.startDate)) {return(true);}
                    break;
                case "endDate" :
                    if (this.matchesDate(searchTerm, this.endDate)) {return(true);}
                    break;
                case "Description" : 
                    if (this.matchesFreeText(searchTerm,this.descriptionLong)) {return(true);}
                    break;
                case "AffectedSlas" : 
                    for (var a:number=0; a < this.affectedServicesInfo.length; a++) {
                        if (this.matchesFreeText(searchTerm, String(this.affectedServicesInfo[a].name))) {
                            return (true);
                        }
                    }
                    break;
                default :
                    break;
            }
        }        
        return (false);
    }
}

/**
* Class for data objects representing external incidents
**/
class ExternalIncident implements IExternalIncident {
    id:string;
    title:string;
    markdown:string;
    parser;
    renderer;
    html: string;

    // constructor () {
    //     // @ts-ignore
    //     this.parser = new commonmark.Parser();
    //     // @ts-ignore       
    //     this.renderer = new commonmark.HtmlRenderer();
    //     this.html = null;
    // }
    
    // /**
    // * Parses the text attribute with a commonmark parser and return the result as a Node object
    // * @return a Node object representing the parsed content of the text attribute
    // **/
    // public getHtml():string {
    //     if (this.html == null) {
    //         var parsed = this.parser.parse(this.markdown);
    //         this.html = this.renderer.render(parsed);                    
    //     }
    //     return(this.html);        
    // }
}

/**
* Class for data objects representing notification messages
**/
class GlobalNews implements IGlobalMessage {
   text:string;
   isExternal:boolean;    
}

class Category implements ICategory {
    id: number;
    name: string;
    services: IService[];
    categories: ICategory[];
    worstState: ServiceState;
    states: Record<ServiceState, number>;
}

class Service implements IService {
    id: number;
    name: string;
    currentStatus: ServiceState;
    incident?: IIncident;
    maintenance?: IMaintanance;
}


class FileState implements IFileState {
    fileType:FileType = null;
    public lastModified:Date = null;
    constructor (fileType:FileType) {
        this.fileType = fileType;        
    }
    isChanged(date:Date):boolean {        
        return ((this.lastModified == null) || (date.getTime() > this.lastModified.getTime()));
    }
}

export {FileType, ISearchableObject, ILoader, FileState, IFileState, IExternalIncidentRaw, IIncident, IParser, Impact, IIncidentRaw, Incident, IMaintanance, Maintanance, IMaintananceRaw, IExternalIncident, ExternalIncident, IGlobalMessage, GlobalNews, IGlobalMessageRaw, IDateFilterable, ICategory, ICategoryRaw, Category, IService, Service, IServiceRaw, ServiceState, ServiceStateNumbers, IServiceTree}